import { z } from "zod"
import { type Address, isAddress } from "viem"
import {
  type VotingType,
  VotingSchema,
  VOTING_TYPE_ENUM,
  PUBLIC_VOTING_STATUS_ENUM,
  VOTING_SCOPE_ENUM,
  VOTING_BLOCKCHAIN_STATUS_ENUM,
} from "@antipartido-inc/contracts-ifaces"
import { VotingOption } from "./VotingOption.js"
import { VotingDoc } from "./VotingDoc.js"
import { TerritorialTarget } from "./TerritorialTarget.js"

const VotingValidation = z.object({
  eligibilityError: z.unknown().optional(),
  id: z.string({ required_error: "id is required" }),
  title: z.string({ required_error: "title is required" }),
  description: z.string({ required_error: "description is required" }),
  type: z.nativeEnum(VOTING_TYPE_ENUM, { required_error: "type is required" }),
  status: z.nativeEnum(PUBLIC_VOTING_STATUS_ENUM, { required_error: "status is required" }),
  scope: z.nativeEnum(VOTING_SCOPE_ENUM, { required_error: "scope is required" }),
  blockchainStatus: z.nativeEnum(VOTING_BLOCKCHAIN_STATUS_ENUM, { required_error: "blockchainStatus is required" }),
  participants: z.number({ required_error: "participants is required" }),
  startDate: z.date({ required_error: "startDate is required" }),
  endDate: z.date({ required_error: "endDate is required" }),
  options: z.array(z.instanceof(VotingOption)),
  tokenName: z.string({ required_error: "tokenName is required" }),
  tokenSymbol: z.string({ required_error: "tokenSymbol is required" }),
  canVote: z.boolean({ required_error: "canVote is required" }),
  hasVoted: z.boolean({ required_error: "hasVoted is required" }),
  docLinks: z.array(z.instanceof(VotingDoc)).optional(),
  territorialTarget: z.instanceof(TerritorialTarget).optional(),
  lastUpdatedBlock: z.number().optional(),
  tokenAddress: z.string().refine(isAddress).optional(),
  merkleRoot: z.string().optional(),
  ipfsUri: z.string().optional(),
  maxParticipants: z.number().optional(),
  pendingAllowlistSafeTxHash: z.string().optional(),
  pendingVoteWindowSafeTxHash: z.string().optional(),
  isAutoUpdatable: z.boolean().optional(),
  allowlistSafeTxHistory: z.array(z.object({ safeTxHash: z.string(), date: z.date() })).optional(),
})

export class Voting {
  constructor(
    private readonly _id: string,
    private readonly _title: string,
    private readonly _description: string,
    private readonly _type: VOTING_TYPE_ENUM,
    private readonly _status: PUBLIC_VOTING_STATUS_ENUM,
    private readonly _scope: VOTING_SCOPE_ENUM,
    private readonly _blockchainStatus: VOTING_BLOCKCHAIN_STATUS_ENUM,
    private readonly _participants: number,
    private readonly _startDate: Date,
    private readonly _endDate: Date,
    private readonly _options: VotingOption[],
    private readonly _tokenName: string,
    private readonly _tokenSymbol: string,
    private readonly _canVote: boolean,
    private readonly _hasVoted: boolean,
    private readonly _docLinks?: VotingDoc[],
    private readonly _territorialTarget?: TerritorialTarget,
    private readonly _lastUpdatedBlock?: number,
    private readonly _tokenAddress?: Address,
    private readonly _merkleRoot?: string,
    private readonly _ipfsUri?: string,
    private readonly _maxParticipants?: number,
    private readonly _pendingAllowlistSafeTxHash?: string,
    private readonly _pendingVoteWindowSafeTxHash?: string,
    private readonly _isAutoUpdatable?: boolean,
    private readonly _allowlistSafeTxHistory?: { safeTxHash: string; date: Date }[],
    private readonly _eligibilityError?: unknown,
  ) {}

  static create({
    id,
    title,
    description,
    type,
    status,
    scope,
    blockchainStatus,
    participants,
    startDate,
    endDate,
    options,
    tokenName,
    tokenSymbol,
    canVote,
    hasVoted,
    docLinks,
    territorialTarget,
    lastUpdatedBlock,
    tokenAddress,
    merkleRoot,
    ipfsUri,
    maxParticipants,
    pendingAllowlistSafeTxHash,
    pendingVoteWindowSafeTxHash,
    isAutoUpdatable,
    allowlistSafeTxHistory,
    eligibilityError,
  }: z.infer<typeof VotingValidation>) {
    const { success, error } = VotingValidation.safeParse({
      id,
      title,
      description,
      type,
      status,
      scope,
      blockchainStatus,
      participants,
      startDate,
      endDate,
      options,
      tokenName,
      tokenSymbol,
      canVote,
      hasVoted,
      docLinks,
      territorialTarget,
      lastUpdatedBlock,
      tokenAddress,
      merkleRoot,
      ipfsUri,
      maxParticipants,
      pendingAllowlistSafeTxHash,
      pendingVoteWindowSafeTxHash,
      isAutoUpdatable,
      allowlistSafeTxHistory,
      eligibilityError,
    })
    if (!success) throw new Error(error.message)
    return new Voting(
      id,
      title,
      description,
      type,
      status,
      scope,
      blockchainStatus,
      participants,
      startDate,
      endDate,
      options,
      tokenName,
      tokenSymbol,
      canVote,
      hasVoted,
      docLinks,
      territorialTarget,
      lastUpdatedBlock,
      tokenAddress as Address | undefined,
      merkleRoot,
      ipfsUri,
      maxParticipants,
      pendingAllowlistSafeTxHash,
      pendingVoteWindowSafeTxHash,
      isAutoUpdatable,
      allowlistSafeTxHistory,
      eligibilityError,
    )
  }

  static createFromAPI({
    id,
    title,
    description,
    type,
    votingStatus,
    scope,
    blockchainStatus,
    participants,
    startDate,
    endDate,
    options,
    tokenName,
    tokenSymbol,
    canVote,
    hasVoted,
    docLinks,
    territorialTarget,
    lastUpdatedBlock,
    tokenAddress,
    merkleRoot,
    ipfsUri,
    maxParticipants,
    pendingAllowlistSafeTxHash,
    pendingVoteWindowSafeTxHash,
    isAutoUpdatable,
    allowlistSafeTxHistory,
    eligibilityError,
  }: VotingType & { eligibilityError?: unknown }) {
    const { success, error } = VotingSchema.safeParse({
      id,
      title,
      description,
      type,
      votingStatus,
      scope,
      blockchainStatus,
      participants,
      startDate,
      endDate,
      options,
      tokenName,
      tokenSymbol,
      canVote,
      hasVoted,
      docLinks,
      territorialTarget,
      lastUpdatedBlock,
      tokenAddress,
      merkleRoot,
      ipfsUri,
      maxParticipants,
      pendingAllowlistSafeTxHash,
      pendingVoteWindowSafeTxHash,
      isAutoUpdatable,
      allowlistSafeTxHistory,
      eligibilityError,
    })
    if (!success) throw new Error(error.message)

    const votingOptions = options.map((option) => VotingOption.createFromAPI(option))
    const docs = docLinks?.map((doc) => VotingDoc.createFromAPI(doc))

    return Voting.create({
      id,
      title,
      description,
      type,
      status: z.nativeEnum(PUBLIC_VOTING_STATUS_ENUM).parse(votingStatus),
      scope,
      blockchainStatus,
      participants,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      options: votingOptions,
      tokenName,
      tokenSymbol,
      canVote: canVote === true,
      hasVoted: hasVoted === true,
      docLinks: docs,
      territorialTarget: territorialTarget ? TerritorialTarget.createFromAPI(territorialTarget) : undefined,
      lastUpdatedBlock: lastUpdatedBlock ?? undefined,
      tokenAddress: tokenAddress ? (tokenAddress as Address) : undefined,
      merkleRoot: merkleRoot ?? undefined,
      ipfsUri: ipfsUri ?? undefined,
      maxParticipants: maxParticipants ?? undefined,
      pendingAllowlistSafeTxHash: pendingAllowlistSafeTxHash ?? undefined,
      pendingVoteWindowSafeTxHash: pendingVoteWindowSafeTxHash ?? undefined,
      isAutoUpdatable: isAutoUpdatable ?? undefined,
      eligibilityError,
      allowlistSafeTxHistory: allowlistSafeTxHistory?.map((entry) => ({ ...entry, date: new Date(entry.date) })),
    })
  }

  getOptionPercentage = (option: VotingOption): number => {
    if (this.totalVotes === 0) {
      return option.percentage ?? 0
    }
    return Math.round((option.votes / this.totalVotes) * 100)
  }

  get id() { return this._id } // prettier-ignore
  get title() { return this._title } // prettier-ignore
  get description() { return this._description } // prettier-ignore
  get type() { return this._type } // prettier-ignore
  get eligibilityError() { return this._eligibilityError } // prettier-ignore
  get status() { return this._status } // prettier-ignore
  get scope() { return this._scope } // prettier-ignore
  get blockchainStatus() { return this._blockchainStatus } // prettier-ignore
  get participants() { return this._participants } // prettier-ignore
  get startDate() { return this._startDate } // prettier-ignore
  get endDate() { return this._endDate } // prettier-ignore
  get options() { return this._options } // prettier-ignore
  get tokenName() { return this._tokenName } // prettier-ignore
  get tokenSymbol() { return this._tokenSymbol } // prettier-ignore
  get canVote() { return this._canVote } // prettier-ignore
  get hasVoted() { return this._hasVoted } // prettier-ignore
  get docLinks() { return this._docLinks } // prettier-ignore
  get territorialTarget() { return this._territorialTarget } // prettier-ignore
  get lastUpdatedBlock() { return this._lastUpdatedBlock } // prettier-ignore
  get tokenAddress() { return this._tokenAddress } // prettier-ignore
  get merkleRoot() { return this._merkleRoot } // prettier-ignore
  get ipfsUri() { return this._ipfsUri } // prettier-ignore
  get maxParticipants() { return this._maxParticipants } // prettier-ignore
  get pendingAllowlistSafeTxHash() { return this._pendingAllowlistSafeTxHash } // prettier-ignore
  get pendingVoteWindowSafeTxHash() { return this._pendingVoteWindowSafeTxHash } // prettier-ignore
  get isAutoUpdatable() { return this._isAutoUpdatable } // prettier-ignore
  get allowlistSafeTxHistory() { return this._allowlistSafeTxHistory } // prettier-ignore
  get winningOption() { return this._options.reduce((prev, current) => (prev.votes > current.votes ? prev : current)) } // prettier-ignore
  get totalVotes() { return this._options.reduce((sum, option) => sum + option.votes, 0)  } // prettier-ignore

  get isCandidateVoting() { return this.type === VOTING_TYPE_ENUM.CANDIDATE } // prettier-ignore
  get isFinished() { return this.status === PUBLIC_VOTING_STATUS_ENUM.CLOSED } // prettier-ignore
  get isUpcoming() { return this.status === PUBLIC_VOTING_STATUS_ENUM.UPCOMING } // prettier-ignore
  get isActive() { return this.status === PUBLIC_VOTING_STATUS_ENUM.ACTIVE } // prettier-ignore
  get isLocal() { return this.scope !== VOTING_SCOPE_ENUM.GLOBAL } // prettier-ignore

  get hasWinnerCandidate() {
    const isCandidateVoting = this.isCandidateVoting
    const isFinished = this.isFinished
    const hasWinner = this.totalVotes > 0 ? this.getOptionPercentage(this.winningOption) >= 51 : true // For proposals or non-finished votings, always show winner

    return isCandidateVoting && isFinished && hasWinner
  }

  get progress(): number {
    const now = new Date().getTime()
    const start = this._startDate.getTime()
    const end = this._endDate.getTime()

    if (now <= start) return 0
    if (now >= end) return 100

    return Math.round(((now - start) / (end - start)) * 100)
  }

  get remainingDays(): number {
    const now = new Date().getTime()
    const end = this._endDate.getTime()
    const diff = end - now
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)))
  }

  serialize() {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      type: this.type,
      status: this.status,
      eligibilityError: this.eligibilityError,
      scope: this.scope,
      blockchainStatus: this.blockchainStatus,
      participants: this.participants,
      startDate: this.startDate,
      endDate: this.endDate,
      options: this.options.map((option) => option.serialize()),
      tokenName: this.tokenName,
      tokenSymbol: this.tokenSymbol,
      canVote: this.canVote,
      hasVoted: this.hasVoted,
      docLinks: this.docLinks?.map((doc) => doc.serialize()),
      territorialTarget: this.territorialTarget?.serialize(),
      lastUpdatedBlock: this.lastUpdatedBlock,
      tokenAddress: this.tokenAddress,
      merkleRoot: this.merkleRoot,
      ipfsUri: this.ipfsUri,
      maxParticipants: this.maxParticipants,
      pendingAllowlistSafeTxHash: this.pendingAllowlistSafeTxHash,
      pendingVoteWindowSafeTxHash: this.pendingVoteWindowSafeTxHash,
      isAutoUpdatable: this.isAutoUpdatable,
      allowlistSafeTxHistory: this.allowlistSafeTxHistory,
    }
  }
}

export type VotingSerialized = ReturnType<Voting["serialize"]>
